
import { Component, Prop, Vue, Emit, Watch } from 'vue-property-decorator'
import QiniuUpload from '@/components/QiniuUpload/index.vue'

// 更新文件类型定义，支持新的七牛SDK
interface ExtendedFile extends File {
  uid?: string // 兼容旧代码
  id?: string  // 新版使用id标识
}

interface UploadProgress {
  [key: string]: number
}

interface MediaChangeEvent {
  checkImages: string[]
  checkVideos: string[]
  referenceImages: string[]
}

interface UploadStatusEvent {
  uploading: boolean
  progress: number
  filesCount: number
}

// 更新上传成功事件接口
interface UploadSuccessEvent {
  url: string
  file: ExtendedFile
  id?: string
  response?: any
}

interface UploadErrorEvent {
  error: Error
  file: ExtendedFile
  id?: string
}

interface UploadProgressEvent {
  file: File
  percentage: number
  id?: string
}

@Component({
  name: 'LeakMediaUpload',
  components: {
    QiniuUpload
  }
})
export default class extends Vue {
  @Prop({ type: String, default: 'leak' }) private mode!: 'leak' | 'repair' // 新增模式属性
  @Prop({ type: Number }) private leakId!: number // 漏点ID
  @Prop({ type: Array, default: () => [] }) private initialCheckImages!: string[] // 初始检测/维修图片
  @Prop({ type: Array, default: () => [] }) private initialCheckVideos!: string[] // 初始检测/维修视频
  @Prop({ type: Array, default: () => [] }) private initialReferenceImages!: string[] // 初始参照物图片

  private refreshKey = Date.now(); // 添加刷新key属性
  private checkImages: string[] = []
  private checkVideos: string[] = []
  private referenceImages: string[] = []
  private localMode: 'leak' | 'repair' = 'leak'
  private localLeakId = -1
  private limits = {
    checkImages: 3,
    checkVideos: 2,
    referenceImages: 3
  }
  private uploadingFiles = 0
  private uploadProgress: UploadProgress = {}

  // 在mounted钩子中初始化图片和视频数据
  mounted() {
    this.initializeMedia();
  }

  // 初始化媒体数据方法
  private initializeMedia() {
    // 复制初始图片和视频到组件内部状态
    this.checkImages = [...this.initialCheckImages];
    this.checkVideos = [...this.initialCheckVideos];
    this.referenceImages = [...this.initialReferenceImages];
    this.localMode = this.mode;
    this.localLeakId = this.leakId;
  }

  // 当props变化时更新内部状态
  @Watch('initialCheckImages')
  onInitialCheckImagesChange() {
    this.checkImages = [...this.initialCheckImages];
  }

  @Watch('initialCheckVideos')
  onInitialCheckVideosChange() {
    this.checkVideos = [...this.initialCheckVideos];
  }

  @Watch('initialReferenceImages')
  onInitialReferenceImagesChange() {
    this.referenceImages = [...this.initialReferenceImages];
  }

  @Watch('mode')
  onModeChange(val: 'leak' | 'repair') {
    this.localMode = val;
  }

  @Watch('leakId')
  onLeakIdChange(val: number) {
    this.localLeakId = val;
  }

  get isUploading(): boolean {
    return this.uploadingFiles > 0
  }

  get totalProgress(): number {
    if (Object.keys(this.uploadProgress).length === 0) return 0
    const total = Object.values(
      this.uploadProgress).reduce((sum, progress) => sum + progress, 0)
    return Math.round(total / Object.keys(this.uploadProgress).length)
  }

  checkImageLimit(type: 'check' | 'reference'): boolean {
    const images = type === 'check' ? this.checkImages : this.referenceImages
    const limit = type === 'check' ?
      (this.localMode === 'repair' ? 6 : this.limits.checkImages) : this.limits.referenceImages

    if (images.length >= limit) {
      this.$message.warning(`最多只能上传${limit}张图片`)
      return false
    }
    return true
  }

  checkVideoLimit(): boolean {
    if (this.checkVideos.length >= this.limits.checkVideos) {
      this.$message.warning(`最多只能上传${this.limits.checkVideos}个视频`)
      return false
    }
    return true
  }

  // 更新处理上传开始事件，兼容新旧版本
  handleUploadStart(file: ExtendedFile): void {
    // 生成唯一ID，如果文件没有uid或id
    const fileId = file.uid || file.id || Date.now() + Math.random().toString(36).substr(2, 9)
    file.uid = fileId as string

    this.uploadingFiles++
    this.uploadProgress[fileId as string] = 0
    this.emitUploadStatus()
  }

  // 更新进度处理方法，兼容新的进度事件格式
  handleUploadProgress(event: UploadProgressEvent): void {
    const { file, percentage, id } = event

    // 使用id作为标识，如果没有则尝试使用file.uid
    const fileId = id || (file as ExtendedFile).uid

    if (fileId) {
      this.uploadProgress[fileId] = percentage
      this.emitUploadStatus()
    }
  }

  // 更新图片上传成功处理方法
  handleImageSuccess(
    type: 'check' | 'reference', eventData: UploadSuccessEvent): void {
    const url = eventData.url
    if (type === 'check') {
      this.checkImages.push(url)
    } else {
      this.referenceImages.push(url)
    }
    this.handleUploadComplete(eventData.file)
    this.emitChange()

    // 更新refreshKey，强制重新渲染上传组件
    this.refreshKey = Date.now()
  }

  // 更新视频上传成功处理方法
  handleVideoSuccess(eventData: UploadSuccessEvent): void {
    const url = eventData.url
    this.checkVideos.push(url)
    this.handleUploadComplete(eventData.file)
    this.emitChange()

    // 更新refreshKey，强制重新渲染上传组件
    this.refreshKey = Date.now()
  }

  // 更新上传完成处理方法
  handleUploadComplete(file: ExtendedFile): void {
    this.uploadingFiles--

    // 使用文件的uid或id作为标识
    const fileId = file.uid || file.id

    if (fileId) {
      delete this.uploadProgress[fileId]
    }

    this.emitUploadStatus()
  }

  // 更新错误处理方法
  handleUploadError(eventData: UploadErrorEvent): void {
    console.error('上传失败:', eventData.error)
    this.handleUploadComplete(eventData.file)
  }

  removeImage(type: 'check' | 'reference', index: number): void {
    if (type === 'check') {
      this.checkImages.splice(index, 1)
    } else {
      this.referenceImages.splice(index, 1)
    }
    this.emitChange()
  }

  removeVideo(index: number): void {
    this.checkVideos.splice(index, 1)
    this.emitChange()
  }

  @Emit('change')
  private emitChange(): MediaChangeEvent {
    return {
      checkImages: this.checkImages,
      checkVideos: this.checkVideos,
      referenceImages: this.localMode === 'leak' ? this.referenceImages : []
    }
  }

  @Emit('upload-status')
  private emitUploadStatus(): UploadStatusEvent {
    return {
      uploading: this.isUploading,
      progress: this.totalProgress,
      filesCount: this.uploadingFiles
    }
  }

  // 新增方法：返回组件当前的所有媒体文件
  getCurrentMedia(): {
    checkImages: string[]
    checkVideos: string[]
    referenceImages: string[]
    repairImages?: string[]
    repairVideos?: string[]
  } {
    if (this.localMode === 'repair') {
      return {
        checkImages: [],
        checkVideos: [],
        referenceImages: [],
        repairImages: this.checkImages,
        repairVideos: this.checkVideos
      }
    } else {
      return {
        checkImages: this.checkImages,
        checkVideos: this.checkVideos,
        referenceImages: this.referenceImages
      }
    }
  }

  // 新增方法：清空所有媒体内容
  resetMedia(): void {
    this.checkImages = [];
    this.checkVideos = [];
    this.referenceImages = [];
    this.uploadingFiles = 0;
    this.uploadProgress = {};
    this.emitChange();

    // 更新刷新key，强制重新渲染上传组件
    this.refreshKey = Date.now();

    // 强制刷新组件，确保上传组件被重新挂载
    this.$nextTick(() => {
      // 触发DOM更新
      this.$forceUpdate();
    });
  }
}
